import React, { useEffect, useMemo, useState } from "react"
import { FileExcelOutlined, ShoppingCartOutlined } from "@ant-design/icons"
import { Col, Divider, Row, Segmented, Tooltip, Typography, Tag } from "antd"
import PlateFilesDrawer from "../../../components/Designer/PlateFilesDrawer"
import { Link as Redirect } from "react-router-dom"
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore"
import { db } from "../../../firebase/config"
import { getStorage, listAll, ref } from "firebase/storage"
import { designerUsersFilter } from "../../../components/Filters/TableFilters"
import { returnProperMachineTag } from "../../../components/utils/ReturnProperMachineTag"
import { OrderAndPrefix } from "../../../components/Orders/Helpers/OrderAndPrefix"
import TapeOrderFill from "./Details/TapeOrderFill"
import CutterDetails from "./Details/CutterDetails"
import FillStopRequest from "../../../components/PaperManager/Orders/Action/FillStopRequest"
import NewDesignOrdersTable from "./NewDesignOrdersTable"
import moment from "moment"
import { orderEndDate } from "../../../components/Table/orderDateDisplay"

const { Title, Link } = Typography

// Checks Firebase Storage for PDFs in vivaPlates/{code}/ and shows a warning icon if none found
function PlateCodeCell({ code, onOpen }) {
  const [hasPDF, setHasPDF] = useState(null)

  useEffect(() => {
    if (!code) return
    let cancelled = false
    listAll(ref(getStorage(), `vivaPlates/${code}`))
      .then((res) => {
        if (cancelled) return
        setHasPDF(res.items.some((f) => f.name.toLowerCase().endsWith(".pdf")))
      })
      .catch(() => {
        if (!cancelled) setHasPDF(null)
      })
    return () => {
      cancelled = true
    }
  }, [code])

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <Link onClick={onOpen}>{code}</Link>
      {hasPDF === false && (
        <Tooltip title="No PDF files in storage">
          <FileExcelOutlined style={{ color: "#ff4d4f", fontSize: 12 }} />
        </Tooltip>
      )}
    </span>
  )
}

// ─── Segment → orderStatus map ───────────────────────────────────────────────
// 999 - STOPPED       998 - ON HOLD        997 - DELETION PROCESS
// 996 - COMPLAINTS    0   - MANAGER        1   - DESIGNER
// 2   - PAPER MANAGER 3   - ARSOMA         3.5 - TAPE
// 4   - VIVA          5   - WINDER         6   - WAREHOUSE
// 6.5 - DELIVERY & PAYMENT                 7   - DELIVERED
const SEGMENT_STATUS = {
  orders: 1, // DESIGNER
  pmanager: 2, // PAPER MANAGER
  arsoma: 3, // ARSOMA
  tape: 3.5, // TAPE
  viva: 4, // VIVA
  winder: 5, // WINDER
  warehouse: 6, // WAREHOUSE
  delivery: 6.5, // DELIVERY & PAYMENT
  done: 7, // DELIVERED
}

// ─── Column definitions passed into OrdersTable ─────────────────────────────
// `render`, `filterable`, `searchable`, `defaultHidden` are all optional.
const ORDER_COLUMNS = [
  {
    key: "orderNumber",
    title: "Nr.",
    dataIndex: "orderNumber",
    width: 70,
    sortable: true,
    filterable: false,
    render: (_, record) => OrderAndPrefix(record),
  },
  {
    key: "jobName",
    title: "Job Name",
    dataIndex: ["selectedProduct", "product", "productName"],
    width: 160,
  },
  {
    key: "client",
    title: "Client",
    dataIndex: ["client", "client"],
    width: 140,
  },
  {
    key: "user",
    title: "Manager",
    dataIndex: "user",
    width: 130,
  },
  {
    key: "plateLocation",
    title: "Plate Location",
    dataIndex: ["selectedPlate", "plateLocation"],
    width: 130,
    defaultHidden: true,
    render: (value, record) =>
      typeof record.selectedPlate === "object" && record.selectedPlate !== null ? (
        value
      ) : (
        <span style={{ opacity: 0.4 }}>—</span>
      ),
  },
  {
    key: "manufacturer",
    title: "Manufacturer",
    dataIndex: ["manufacturer", "company"],
    width: 140,
  },
  {
    key: "material",
    title: "Material",
    dataIndex: ["selectedProduct", "material"],
    width: 110,
  },
  {
    key: "machine",
    title: "Machine",
    dataIndex: ["selectedProduct", "machine"],
    width: 100,
    render: (value) => returnProperMachineTag(value),
  },
  {
    key: "knifeCode",
    title: "Knife",
    dataIndex: ["selectedProduct", "knifeCode"],
    width: 120,
    render: (value) => value ?? <span style={{ opacity: 0.4 }}>—</span>,
  },
  {
    key: "plateCode",
    title: "Plate",
    dataIndex: ["selectedPlate", "code"],
    width: 110,
    render: (value, record) =>
      typeof record.selectedPlate === "object" && record.selectedPlate !== null ? (
        value
      ) : (
        <span style={{ opacity: 0.4 }}>—</span>
      ),
  },
  {
    key: "technicalPage",
    title: "Technical",
    dataIndex: ["selectedProduct", "technicalPage"],
    width: 100,
    filterable: false,
    searchable: false,
    render: (value) =>
      value ? (
        <Link rel="noreferrer" target="_blank" href={value}>
          View
        </Link>
      ) : (
        <span style={{ opacity: 0.4 }}>—</span>
      ),
  },
  {
    key: "created",
    title: "Created",
    dataIndex: "created",
    width: 110,
    filterable: false,
    sorter: (a, b) => a.created.seconds - b.created.seconds,
    render: (value) => moment.unix(value.seconds).format("DD/MM/YYYY"),
  },
  {
    key: "completedTime",
    title: "Ended",
    dataIndex: "completedTime",
    width: 110,
    filterable: false,
    sorter: (a, b) => {
      if (a.completedTime && b.completedTime)
        return a.completedTime.seconds - b.completedTime.seconds
      if (a.completedTime && !b.completedTime) return -1
      if (!a.completedTime && b.completedTime) return 1
      return 0
    },
    render: (value, record) => orderEndDate(value, record),
  },
]

// ─── Component ───────────────────────────────────────────────────────────────

export default function NewDesignerOrders() {
  const [segment, setSegment] = useState("orders")
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedPlateCode, setSelectedPlateCode] = useState(null)

  const columns = useMemo(
    () =>
      ORDER_COLUMNS.map((col) =>
        col.key === "plateCode"
          ? {
              ...col,
              render: (value, record) =>
                typeof record.selectedPlate === "object" && record.selectedPlate !== null ? (
                  <PlateCodeCell code={value} onOpen={() => setSelectedPlateCode(value)} />
                ) : (
                  <span style={{ opacity: 0.4 }}>—</span>
                ),
            }
          : col,
      ),
    [],
  )

  // Subscribe to Firestore whenever segment changes
  useEffect(() => {
    setLoading(true)
    const ordersRef = collection(db, "orders")
    const status = SEGMENT_STATUS[segment]
    const isDone = segment === "done"

    // Done orders have inconsistent completedTime types so we can't use orderBy on them
    const q = isDone
      ? query(ordersRef, where("orderStatus", "==", status))
      : query(ordersRef, where("orderStatus", "==", status), orderBy("created", "desc"))

    const unsub = onSnapshot(q, (snapshot) => {
      let results = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }))

      if (isDone) {
        // Filter out system/test account
        results = results.filter((o) => o.uid !== "E1XNaWvT6PccXRSKB3JNlNehI362")
        // Sort by completedTime desc on the frontend
        results = results.sort((a, b) => {
          const getTs = (v) => {
            if (!v) return 0
            if (v.seconds) return v.seconds
            if (v instanceof Date) return v.getTime() / 1000
            if (typeof v === "string") return new Date(v).getTime() / 1000
            return 0
          }
          return getTs(b.completedTime) - getTs(a.completedTime)
        })
      }

      setOrders(results)
      setLoading(false)
    })

    return () => unsub()
  }, [segment])

  // ─── Per-row actions column ─────────────────────────────────────────────────
  const renderActions = (record) => {
    const machine = record.selectedProduct?.machine
    const type = record.orderType

    let action = null
    if (machine === "Cutter") {
      action = <CutterDetails order={record} />
    } else if (type === "Label") {
      action = (
        <Redirect state={record} to={record.id}>
          View
        </Redirect>
      )
    } else if (type === "Tape") {
      action = <TapeOrderFill order={record} />
    }

    return (
      <>
        {action}
        {record.requestedToStop && <FillStopRequest data={record} />}
      </>
    )
  }

  return (
    <div style={{ padding: 16 }}>
      <Row gutter={[0, 16]}>
        {/* Header */}
        <Col span={24}>
          <Title level={4} style={{ margin: 0 }}>
            <ShoppingCartOutlined />
            <Divider type="vertical" />
            All Orders
          </Title>
        </Col>

        {/* Segment selector */}
        <Col span={24}>
          <Segmented
            value={segment}
            onChange={setSegment}
            options={[
              { label: "Designer", value: "orders" },
              { label: "Paper Manager", value: "pmanager" },
              { label: "Arsoma", value: "arsoma" },
              { label: "Viva", value: "viva" },
              { label: "Tape", value: "tape" },
              { label: "Winder", value: "winder" },
              { label: "Warehouse", value: "warehouse" },
              { label: "Delivery & Payment", value: "delivery" },
              { label: "Done", value: "done" },
            ]}
          />
        </Col>

        {/* Table */}
        <Col span={24}>
          <NewDesignOrdersTable
            data={orders}
            columns={columns}
            loading={loading}
            rowKey="id"
            renderActions={segment === "orders" ? renderActions : undefined}
          />
        </Col>
      </Row>
      <PlateFilesDrawer
        code={selectedPlateCode}
        open={!!selectedPlateCode}
        onClose={() => setSelectedPlateCode(null)}
      />
    </div>
  )
}
